import CodeMirror from 'codemirror';

import "codemirror/lib/codemirror.css"
import 'codemirror/mode/xml/xml.js'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/css/css.js'
import 'codemirror/mode/htmlmixed/htmlmixed.js'
import 'codemirror/mode/django/django.js'

import 'codemirror/addon/lint/lint.css'
import 'codemirror/addon/lint/lint.js'

import 'codemirror/addon/edit/matchbrackets.js'
import 'codemirror/addon/fold/xml-fold.js'
import 'codemirror/addon/edit/closetag.js'
import 'codemirror/addon/edit/closebrackets.js'
import 'codemirror/addon/edit/matchtags.js'

import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/show-hint.js'

import 'codemirror/addon/hint/xml-hint.js'
import 'codemirror/addon/hint/html-hint.js'

import '../utils/hints/curatedplanet-hint'

import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/foldgutter.js'
import 'codemirror/addon/fold/brace-fold.js'
import 'codemirror/addon/fold/indent-fold.js'
import 'codemirror/addon/fold/comment-fold.js'

import 'codemirror/addon/dialog/dialog.js'
import 'codemirror/addon/dialog/dialog.css'

import 'codemirror/addon/search/searchcursor.js'
import 'codemirror/addon/search/search.js'
import 'codemirror/addon/search/matchesonscrollbar.css'


import 'codemirror/keymap/sublime.js'

import "../codeeditor.css"
import { setCuratedPlanetKeywords, setCuratedPlanetMacros } from '../utils/hints/keywords';
import { useEffect, useRef } from 'react';

const KEYWORD_FILTER_LIST = ['properties', 'description', 'type', 'format', 'items', 'examples']

function getKeysAndMacros(schema, leafNodes, macros) {
    iterateSchema(schema.properties, '');
    function iterateSchema(node, path) {
        if (Object(node) === node) {
            const { items, description, properties, format } = node;
            if (items) {
                if (items.properties) {
                    for (const [key, value] of Object.entries(items.properties)) {
                        const {type, properties} = value;
                        if (type != "object") {
                            pushMacro(macros, path, key);
                        } else if (properties) {
                            deepRecursion(path, key, value.properties);
                        }
                    }
                } else {
                    var keys = Object.keys(items);
                    for (const key of keys) {
                        pushMacro(macros, path, key);
                    }
                }
            } else if (!properties && description && (typeof description === 'string' || description instanceof String)) {
                if ((leafNodes.length == 0) || !leafNodes.some(e => e.path === path)) {
                    var appendSafe = (format=='html' ? '|safe' : '');
                    pushLeafNode(leafNodes, path, appendSafe);
                }
            }
            for (const [key, value] of Object.entries(node)) {
                if (key === 'oneOf') {
                    for (var i=0; i<value.length; i++) {
                        iterateSchema(value[i], path);
                    }
                } else {
                    if (key !== 'description' && typeof value === 'object' && value !== null) {
                        iterateSchema(value, KEYWORD_FILTER_LIST.includes(key) ? path : (path ? path + '.' + key : key));
                    }
                }
            }    
        }

        function pushLeafNode(leafNodes, path, appendSafe) {
            leafNodes.push({displayText: `${path}`, text: `${path}${appendSafe}`});
        }

        function pushMacro(macros, path, key) {
            macros.push({displayText: `${path}.${key}`, text: ` for item in ${path} %} {{${key}}} {% endfor `});
        }

        function deepRecursion(base, parent, properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (value.type != "object") {
                    macros.push({displayText: `${base}.${parent}.${key}`, text: ` for item in ${base} %} {{${parent}.${key}}} {% endfor `});
                }
            }
        };    
    }
}

export function updateKeysAndMacros(schema) {
    var leafNodes = [];
    var macros = [];
    getKeysAndMacros(schema, leafNodes, macros);
    setCuratedPlanetKeywords(leafNodes);
    setCuratedPlanetMacros(macros);
    return leafNodes.length + macros.length;
}

export function insertTextAtCursor(editor, text) {
    var doc = editor.getDoc();
    var cursor = doc.getCursor();
    doc.replaceRange(text, cursor);
}

export const useEffectRunOnce = ( effect => {

    const destroyFunc = useRef();
    const calledOnce = useRef(false);
    const renderAfterCalled = useRef(false);

    if (calledOnce.current) {
        renderAfterCalled.current = true;
    }

    useEffect( () => {
        if (calledOnce.current) { 
            return; 
        }

        calledOnce.current = true;
        destroyFunc.current = effect();

        return ()=> {
            if (!renderAfterCalled.current) {
                return;
            }

            if (destroyFunc.current) {
                destroyFunc.current();
            }
        };
    }, []);
});

export const getCodeEditorValue = (cm) => {
    return cm?.getDoc().getValue();
}

export const setCodeEditorValue = (cm, value) => {
    cm?.getDoc().setValue(value);
}

function countSearchResults(editor, query) {
    const cursor = editor.getSearchCursor(query);
    let count = 0;
    while (cursor.findNext()) {
        count++;
    }
    return count;
}

export const CodeEditor = ({id, name, defaultValue, setCm, setIsChanged}) => {

    function initializeFromTextArea(id, setIsChanged) {

        var cm = (CodeMirror.fromTextArea(document.getElementById(id), {
            mode:  "django",
            theme: "idea",
            keyMap: "sublime",
            indentUnit: 4,
            smartIndent: true,
            tabSize: 4,
            indentWithTabs: false,
            lineWrapping: true,
            lineNumbers: true,
            autofocus: true,
            matchBrackets: true,
            autoCloseTags: true,
            foldGutter: true,
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
            autoCloseBrackets: {
                pairs: "()[]{}%%''\"\""
            },
            showHint: true,
            extraKeys: {"Ctrl-Space": "autocomplete", "Cmd-Space": "autocomplete"},
            hintOptions: {completeSingle: false},
        }));
    
        cm.setSize('100%', '100vh');
    
        cm.on('change', cm => {
            setIsChanged(true);
        });
    
        cm.on("inputRead", function(cm) {
            if (cm.state.completionActive) {
                return;
            }
            CodeMirror.showHint(cm, CodeMirror.hint.curatedplanet);
        });
        
        function updateFooterWithSearchCount(query) {
            const searchWrapper = document.getElementById('search-result-wrapper');
            const searchResult = document.getElementById('search-result-count');

            searchWrapper.style.display = 'none';

            if (!query) {
                searchWrapper.style.display = 'none';
                return;
            }
            searchWrapper.style.display = 'block';
            const count = countSearchResults(cm, query);
            if (count == 0) {
                searchResult.textContent = `No matches found for "${query}"`;
                return;
            }
            const cursor = cm.getSearchCursor(query);
            let currentMatch = 0;
            while (cursor.findNext()) {
                currentMatch++;
                if (cursor.from().line === cm.getCursor().line && cursor.from().ch === cm.getCursor().ch - query.length) {
                    break;
                }
            }
            searchResult.textContent = `Matches for "${query}"  ${currentMatch}/${count}`;
        }

        cm.on('cursorActivity', () => {
            const query = cm.state.search && cm.state.search.query;
            updateFooterWithSearchCount(query);
        });

        return cm;
    };

    useEffectRunOnce( ()=> {
        setCm(initializeFromTextArea(id, setIsChanged));
        setIsChanged(false);
    });

    return (
      <textarea id={id} name={name} defaultValue={defaultValue}></textarea>
    )
}